/* eslint-disable @tencent/i18next/no-literal-string */
import React from 'react';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IHistoryManager, IHistoryEventMap, HistoryEventType, IHistoryManagerInjectable, IShowHistoryConfig } from './history-manager.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { SlideFile, type ICommandData, type IMutationData, type ISlideFileData, MutationType, STPageType, type IPresentationDataModel, MutationSource } from '@tencent/slide-sdk-model';
import { ILockService } from '@src/slide/application/service/lock-service/lock-service.interface';
import { SubscribableService } from '@src/slide/application/service/subscribable-service';
import { ISelectedAnimManager } from '@src/slide/application/service/selection/selected-anim-manager.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IPresentationStoreService } from '@src/slide/domain/slide-app/core/interfaces/presentation-service.interface';
import HistoryRSBarModule from '@src/slide/application/service/history-manager/core/history-rsbar-module';
import HistoryMobileModule from '@src/slide/application/service/history-manager/core/history-mobile-module';
import { HISTORY_COLOR_LIST, HISTORY_MODULE_NAME, NOHISTORY_HEATCODE, RecordTypeKey } from '@src/slide/application/service/history-manager/core/constants';
import { reaction } from 'mobx';
import TaskManager from '@src/slide/infrastructure/module/dependent/taskManager';
import ReactDOM from 'react-dom';
import HistoryWrapper from '@src/slide/application/service/history-manager/core/container';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { RevisionData } from '@tencent/slide-sdk-model/src/app/revision-data';
import { assert } from 'ts-essentials';
import { tdw } from '@tencent/tencent-doc-report';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import i18n from '@i18next/i18n';
import CommonTips, { CommonTipsType } from '@src/framework/frame/components/common-tips';
import { IExportManager } from '@src/slide/application/service/export-manager/core/interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { HistoryLoadingMask, IHistoryLoadingMaskProps } from '@src/slide/application/service/history-manager/core/mask';
import HistoryRevertListener from '@src/slide/application/service/history-manager/core/history-revert-listener';
import { IAccusationService } from '@src/slide/application/service/accusation-service/core/accusation-service.interface';
import HistoryAuthorTag, { IHistoryAuthorTagProps } from '@src/slide/application/service/history-manager/core/components/author-tag';
import { SnapshotRecovery } from '@src/slide/application/service/history-manager/core/container/snapshot-recovery';
import { IRightSideBarService } from '../../right-side-bar-service/core/interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import _get from 'lodash/get';
import { SlideDataMigrator } from '@tencent/slide-sdk-data-migration';
import { SnapshotDataItemsModel } from '@tencent/slide-sdk-persistent/src/snapshot-manager';
import { IMenuManager } from '../../menu-manager/menu-manager.interface';

export class HistoryManager extends SubscribableService<IHistoryEventMap> implements IHistoryManager {
    public canUpdateEditorState = true;
    public isShowingHistory = false;
    public isShowHistoryPanel = false;
    public modification: any;
    public sectionModification: any;
    public changedComponents: Array<any>;
    public recordType: string;
    private colorCandidateList: Array<string>;
    private colorMap: any;
    private preHistorySelectedSlideIndex: number;
    private presentation: any;
    private activeId: string | null;
    private showTab: boolean | null;
    private authors: any;
    private rawPresentation: IPresentationDataModel | null | undefined;
    private presentationStoreService: IPresentationStoreService | null;
    private historyMobileModule;

    public constructor(
        private uiDataService: IUiDataService,
        private selectionService: ISelectionService,
        private exportManager: IDynamicService<IExportManager>,
        private userPriviledgeService: IUserPriviledgeService,
        private lockService: ILockService,
        private accusationService: IDynamicService<IAccusationService>,
        private rightSideBarService: IRightSideBarService,
        private menuManager: IMenuManager,
        private injectable: IHistoryManagerInjectable,
        private selectedAnims?: ISelectedAnimManager,
    ) {
        super();
        rightSideBarService.registerModule(new HistoryRSBarModule(this));
        // colors
        this.colorCandidateList = HISTORY_COLOR_LIST.slice(0);
        this.colorMap = {};
        this.changedComponents = [];
        this.init();
        this.initHistoryRevertListeners();
        this.injectable.listenStatusChange?.();
        this.historyMobileModule = new HistoryMobileModule(this);
        this.injectable.versionPreviewInvoke?.(this.show.bind(this));
    }

    public init(): void {
        // 修订记录：拉取完历史数据后的通知
        this.on(HistoryEventType.FETCHED, () => {
            // 如果在修订记录状态（保护逻辑）
            if (this.isShowHistoryPanel) {
                // 如果是第一次进入修订记录，记录一下当前选中的页
                if (!this.isShowingHistory && this.selectionService) {
                    this.preHistorySelectedSlideIndex = this.selectionService.currentSlideIndex;
                }
                this.isShowingHistory = true;
                // 清空选区
                this.selectionService.selectedSlides.clear();
                this.selectionService.selectedElements.clear();
                this.selectedAnims?.clear();
                // 计算第一个被修改的幻灯片是第几页
                let firstModified = -1;
                for (let i = this.modification.length - 1; i >= 0; i--) {
                    const modify = this.modification[i];
                    if (modify.length > 0) {
                        firstModified = i;
                        break;
                    }
                }
                if (firstModified === -1) {
                    firstModified = Math.max(this.modification.length - 1, 0);
                }
                // const { presentation } = HistoryManager.getInstance();
                // 监听slide初始化，每增加1页时，判断是否是第一个修改的页，如果是，则选中
                reaction(() => this.selectionService.slides, (slides, reaction) => {
                    if (!slides) return;
                    if (slides.length - 1 === firstModified) {
                        this.selectionService.selectedSlides
                            .add(this.uiDataService.presentation?.slideOfIndex(firstModified));
                        if (this.selectionService) {
                            this.selectionService.currentSlideIndex = firstModified;
                        }
                        reaction.dispose();
                    } else if (slides.length === this.presentation.slideList.length) {
                        reaction.dispose();
                    }
                });
                if (this.selectionService) {
                    this.selectionService.currentSlideIndex = firstModified;
                }
                // 标记进入历史版本
                this.userPriviledgeService.updateHistoricalVersionStatus(true, true);
                // 取消所有任务
                TaskManager.cancelAllTasks();
                // 应先解除presentation绑定的SDK
                this.uiDataService.presentation?.unbindSDK();
                // 应用历史数据（异步）
                this.uiDataService.presentation?.bindSDK(this.presentation);
            }
        });

        // 修订记录：面板关闭通知
        this.on(HistoryEventType.HIDE, () => {
            if (this.isShowingHistory === false) {
                return;
            }
            this.isShowingHistory = false;
            const presentation = this.rawPresentation;
            // 还原打开修订记录前选中的页：监听slide初始化，每增加1页时，判断是否是应当选中的页，如果是，则选中
            presentation && reaction(() => this.selectionService.slides, (slides, reaction) => {
                if (!slides) return;
                if (slides.length - 1 === this.preHistorySelectedSlideIndex) {
                    this.selectionService.selectedSlides
                        // eslint-disable-next-line max-len
                        .add(this.uiDataService.presentation?.slideOfIndex(this.preHistorySelectedSlideIndex));
                    if (this.selectionService) {
                        this.selectionService.currentSlideIndex = this.preHistorySelectedSlideIndex;
                    }
                    reaction.dispose();
                } else if (slides.length === presentation.slideList.length) {
                    reaction.dispose();
                }
            });
            // 清空选区
            this.selectionService.selectedSlides.clear();
            this.selectionService.selectedElements.clear();
            this.selectedAnims?.clear();
            // 恢复历史版本状态
            this.userPriviledgeService.updateHistoricalVersionStatus(false, this.canUpdateEditorState);
            this.canUpdateEditorState = true;
            // 恢复选中的页
            if (this.selectionService) {
                this.selectionService.currentSlideIndex = this.preHistorySelectedSlideIndex;
            }
            // 检查锁状态
            this.lockService?.checkLock();
            // 取消所有任务
            TaskManager.cancelAllTasks();
            // 应先解除presentation绑定的SDK
            this.uiDataService.presentation?.unbindSDK();
            // 应用编辑数据（异步）
            this.uiDataService.presentation?.bindSDK(this.rawPresentation);
            // 清空原始presentation
            this.rawPresentation = null;
        });
    }

    public initHistoryRevertListeners(): void {
        new HistoryRevertListener(this);
    }

    public showModule(DOMNode: Element, onShow: () => void, onClose: () => void) {
        ReactDOM.render(<HistoryWrapper
            onClose={() => {
                onClose?.();
            }}
            accusationService={this.accusationService}
            getVersion={this.fetchVersion.bind(this)}
            getHistorySlide={this.fetchHistorySlide.bind(this)}
            getColor={this.getColorForId.bind(this)}
            revert={this.revert.bind(this)}
            getHistory={this.getHistory.bind(this)}
            getAuthor={this.getAuthor.bind(this)}
            activeId={this.activeId}
            type={this.recordType}
            isSupportArkVersionManager={this.injectable.isSupportArkVersionManager}
            exportManager={this.exportManager}
            enableHistoryVersion={this.injectable.enableHistoryVersion}
            showTab={this.showTab}
            injectable={this.injectable}
        />, DOMNode);
        this.authors = {};
        onShow?.();
        this.isShowHistoryPanel = true;
        this.emit(HistoryEventType.SHOW, undefined);
    }

    public hideModule(DOMNode: Element) {
        ReactDOM.unmountComponentAtNode(DOMNode);
        this.isShowHistoryPanel = false;
        this.presentationStoreService?.dispose();
        this.presentationStoreService = null;
        this.presentation = null;
        this.modification = null;
        // 左侧列表节相关修改。本应耦合与 modification 一起，但是为了向下兼容其他场景故单独开一个 api
        this.sectionModification = null;
        this.authors = null;
        this.emit('hide', undefined);
        if (this.rightSideBarService.isShown(HISTORY_MODULE_NAME)) {
            this.rightSideBarService.hide();
        }
    }

    // 支持show的时候设置当前显示的是[版本]还是[历史]
    public show(configs?: IShowHistoryConfig) {
        const { activeId, showTab, type } = configs || {};
        this.activeId = activeId || null;
        // showTab为false时才隐藏，默认显示历史-版本栏
        this.showTab = showTab === false ? false : null;
        this.recordType = type || RecordTypeKey.DEFAULT;
        this.rightSideBarService.show(HISTORY_MODULE_NAME, undefined);
    }

    public mobileShow(configs?: IShowHistoryConfig) {
        const { activeId, showTab, type } = configs || {};
        this.activeId = activeId || null;
        // showTab为false时才隐藏，默认显示历史-版本栏
        this.showTab = showTab === false ? false : null;
        this.recordType = type || RecordTypeKey.DEFAULT;
        this.historyMobileModule.show();
    }

    public isShown() {
        return this.rightSideBarService.isShown(HISTORY_MODULE_NAME);
    }

    public handleFetchException(e: Error, fail?: () => void) {
        window.log?.error(e);
        fail?.();
    }

    /**
     * 回滚到指定版本
     * @param {版本号} rev
     * @param {大版本号} version
     */
    public async revert(rev, version, success, fail) {
        this.emit(HistoryEventType.REVERT_BEGIN, undefined);
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.revert(this.globalPadId, rev, version, 30000).then(async (response) => {
            const res = response.data;
            if (res.retcode === 0) {
                tdw({
                    opername: 'doc_core',
                    module: 'edit',
                    action: 'recover_his',
                });
                success?.();
                await OnlineEditServices.CollaborationManager()?.getPersistent()
                    .replaceAllData([]);
                setTimeout(() => {
                    const shortCircuit = this.injectable.onBeforeRevertUrlReload();
                    if (shortCircuit) {
                        return;
                    }
                    tencentDocOpenUrlReload();
                }, 1000);
            } else {
                fail?.();
            }
            this.emit(HistoryEventType.REVERT_END, undefined);
        })
            .catch((error) => {
                fail?.(error);
                this.emit(HistoryEventType.REVERT_END, undefined);
            });
    }

    /**
     * 拉取浏览记录
     * @param {第几页} page
     * @param {成功回调} success
     * @param {失败回调} failed
     */
    public async getHistory(page: number, success: (res: any) => void, failed: (res: any) => void) {
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.getHistory(this.globalPadId, page).then((response) => {
            const res = response.data;
            if (res.retcode === 0) {
                const { authors } = res;
                const newAuthors = { ...this.authors };
                // eslint-disable-next-line no-restricted-syntax
                for (const id in authors) {
                    if (!newAuthors[id]) {
                        newAuthors[id] = authors[id];
                    }
                }
                this.authors = newAuthors;
                success?.(res);
            } else {
                if (res.retcode === NOHISTORY_HEATCODE) {
                    const commonTipsConfig = {
                        message: i18n.t('在线人数较多，暂时无法查看修订记录'),
                        commonTipsType: CommonTipsType.INFO,
                        block: false,
                        closable: true,
                    };
                    CommonTips.getInstance().show(commonTipsConfig);
                }
                failed?.(res);
            }
        })
            .catch((error) => {
                failed?.(error);
            });
    }

    /**
     * 获取作者信息
     * @param {作者id} id
     */
    public getAuthor(id) {
        if (this.authors) {
            return this.authors[id];
        }
        return null;
    }

    /**
     * 取颜色，每次从颜色表随机取一个
     * @param {用户id} id
     */
    public getColorForId(id) {
        if (!this.colorMap[id]) {
            if (this.colorCandidateList.length <= 0) {
                this.colorCandidateList = HISTORY_COLOR_LIST.slice(0);
            }
            const index = Math.floor((Math.random() * this.colorCandidateList.length));
            this.colorMap[id] = this.colorCandidateList[index];
            this.colorCandidateList.splice(index, 1);
        }
        return this.colorMap[id];
    }

    public renderHistoryLoadingMask(props?: Partial<IHistoryLoadingMaskProps>): React.ReactElement {
        return <HistoryLoadingMask {...props} historyManager={this} />;
    }

    public renderHistoryAuthorTag(props?: Partial<IHistoryAuthorTagProps>): React.ReactElement {
        return <HistoryAuthorTag {...props} />;
    }

    public renderSnapshotRecovery(): React.ReactElement {
        const downloadPPT = () => this.menuManager.actionNativePPT();
        const exportSnapshotFile = (snapshot: SnapshotDataItemsModel) => this.exportManager.promise()
            .then(e => e.exportSnapshotFile(snapshot));
        return <SnapshotRecovery downloadPPT={downloadPPT} exportSnapshotFile={exportSnapshotFile} />;
    }

    private get globalPadId() {
        return SlideDataServices.DocInfoManager()?.globalPadId ?? '';
    }

    /**
     * 拉取文档版本信息
     * @param {*} page
     * @param {*} success
     * @param {*} fail
     */
    private async fetchVersion(page: number, success?: (res: any) => void, fail?: () => void) {
        const limit = 20;
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.getVersion(this.globalPadId, page, limit)
            .then((response) => {
                const res = response.data;
                if (res.retcode === 0) {
                    success?.(res);
                } else {
                    fail?.();
                }
            })
            .catch(() => {
                fail?.();
            });
    }

    private convertActionList(actionList: Array<{ author: string, action: string }>) {
        return actionList.map(({ author, action }) => {
            const mutations = RevisionData.fromBufferOrBase64(action).getValidMutationData();
            const changedComponents = mutations.map(({ pageId, shapeId }: any) => shapeId || pageId || '');
            return {
                author,
                commandData: new SlideFile({ presentation: action }).getCommandData(),
                mutations,
                changedComponents,
            };
        });
    }

    private getNewPresentationStoreServiceFromText(slideFileData: ISlideFileData) {
        const slideFile = new SlideFile(slideFileData);
        const presentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        assert(presentationStoreService, 'presentationStoreService is not exist');
        return presentationStoreService.createFromCommandData(slideFile.getCommandData());
    }

    private async getHistorySlideV3(rev, fRev, success, fail) {
        this.emit(HistoryEventType.FETCH_BEGIN, undefined);
        try {
            const apiService = await OnlineEditServices.OnlineApiService.promise();
            window.log.info(`select history from:${fRev}, to:${rev}`);
            // 直接拉取最后一个版本，直接使用keyframe;
            const res = await apiService.getHistorySlide(this.globalPadId, rev, fRev);
            if (res?.data?.retcode !== 0 || !res?.data?.data?.initialAttributedText?.text) throw new Error('拉取数据失败');
            const { initialAttributedText, actionlist = [] } = res.data.data;
            const data = JSON.parse(initialAttributedText.text);
            if (data.dver >= '3.0.0') {
                this.handleHistoryV3(data, actionlist);
            } else {
                // 2.0存量文档升级后，历史记录仍然是2.0数据，前端需要兼容2.0数据预览。
                // 因为3.0制品已经没有应用2.0数据的能力，所以这里用rev多拉一次作为最新数据。
                const presentationOld = this.makeKeyframeV2(initialAttributedText.text);
                if (!presentationOld) throw new Error(`历史记录2.0[${fRev}: ${rev}]：初始化修改前数据失败`);
                const res = await apiService.getHistorySlide(this.globalPadId, rev, rev);
                if (res?.data?.retcode !== 0 || !res?.data?.data?.initialAttributedText?.text) throw new Error(`历史记录2.0[${fRev}: ${rev}]：拉取修改后数据失败`);
                const presentationNew = this.makeKeyframeV2(res.data.data.initialAttributedText?.text);
                if (!presentationNew) throw new Error(`历史记录2.0[${fRev}: ${rev}]：初始化修改后数据失败`);
                this.handleHistroyV2(presentationOld, presentationNew, actionlist);
            }
            this.emit(HistoryEventType.FETCHED, undefined);
            success?.();
        } catch (e) {
            fail?.();
            window.log.warn(e);
        }
        this.emit(HistoryEventType.FETCH_END, undefined);
    }

    private getListChangedByAuthor(slideListLength: number, sectionListLength: number, actionList: {
        author: string; commandData: Omit<ICommandData, 'mutations'> & Required<
                Pick<ICommandData, 'mutations'>>; mutations: IMutationData[]; changedComponents: any[];
    }[]) {
        const changedSlideList: Array<Array<string>> = new Array(slideListLength).fill(0)
            .map(() => ([]));
        const changedSectionList: Array<Array<string>> = new Array(sectionListLength).fill(0)
            .map(() => ([]));
        actionList.forEach(({ mutations, author }) => {
            // eslint-disable-next-line complexity
            mutations.forEach((mutation: any) => {
                if (mutation.pageType === STPageType.slide) {
                    if (mutation.type === MutationType.AddPage) {
                        changedSlideList.splice(mutation.index, 0, [author]);
                        return;
                    }
                    if (mutation.type === MutationType.RemovePage) {
                        changedSlideList.splice(mutation.index, 1);
                        return;
                    }
                    if (mutation.type === MutationType.MoveAnimation) {
                        changedSlideList[mutation.index] = [author];
                        return;
                    }
                }
                if (mutation.pageId) {
                    const slide = this.presentation.getSlideById(mutation.pageId);
                    if (slide) {
                        const index = this.presentation.slideList.indexOf(slide);
                        const authorList = changedSlideList[index];
                        authorList && !authorList.includes(author) && authorList.push(author);
                    }
                }
                if (mutation.type === MutationType.AddSection) {
                    changedSectionList.splice(mutation.index, 0, [author]);
                    return;
                }
                if (mutation.type === MutationType.RemoveSection) {
                    changedSectionList.splice(mutation.index, 1);
                    return;
                }
                if (mutation.type === MutationType.SetSectionProperties) {
                    const authorList = changedSectionList[mutation.index];
                    authorList && !authorList.includes(author) && authorList.push(author);
                    return;
                }
            });
        });
        return {
            changedSlideList,
            changedSectionList,
        };
    }

    private beforeFetchHistorySlide() {
        if (!this.rawPresentation) {
            this.rawPresentation = this.uiDataService.presentation.sdkData;
        }
        if (this.presentationStoreService) {
            this.presentationStoreService.dispose?.();
        }
    }

    /**
     * 拉取历史版本数据，并拉取两个版本之间的变更
     * @param {版本号} rev
     * @param {上一个版本号} fRev
     */
    private fetchHistorySlide(rev, fRev, success?: () => void, fail?: () => void) {
        this.beforeFetchHistorySlide();
        return this.getHistorySlideV3(rev, fRev, success, fail);
    }

    /**
     * 处理V3版本历史记录
     * @param initialAttributedText 修订记录初始presentation
     * @param actionList 变更记录
     */
    private handleHistoryV3(initialAttributedText: ISlideFileData, actionList: Array<{
        author: string;
        action: string;
    }>) {
        const presentationStoreService = this.getNewPresentationStoreServiceFromText(initialAttributedText);
        this.presentationStoreService = presentationStoreService;
        this.presentation = presentationStoreService.getPresentation();
        const slideListLength = this.presentation.slideList.length;
        const sectionListLength = this.presentation.sectionList.length;
        const actions = this.convertActionList(actionList);
        presentationStoreService.getTransaction()(() => actions
            .map(({ commandData }) => presentationStoreService
                .applyCommandData(commandData, MutationSource.Keyframe)));
        const { changedSlideList, changedSectionList } = this.getListChangedByAuthor(
            slideListLength,
            sectionListLength,
            actions,
        );
        this.changedComponents = actions.map(action => ({
            components: new Set(action.changedComponents),
            author: action.author,
        }));
        this.modification = changedSlideList;
        this.sectionModification = changedSectionList;
    }

    /**
     * 处理V2版本历史记录
     * @param initialAttributedText 修订记录初始presentation
     * @param actionList 变更记录
     */
    private handleHistroyV2(presentationOld: any, presentationNew: any, actionList: Array<{
        author: string;
        action: string;
    }>) {
        const oldSlideLength = presentationOld.slideList.length;
        const oldSectionLength = presentationOld.sectionList?.length || 0;
        const actions: Array<any> = [];
        this.changedComponents = [];
        actionList.forEach((a) => {
            const action = JSON.parse(a.action);
            actions.push({ author: a.author, changeset: action });
            const changedCompoent = {
                author: a.author,
                components: this.findChangedComponentByChangesetV2(action),
            };
            this.changedComponents.push(changedCompoent);
        });
        this.modification = this.getModifiedPageOrSectionV2(oldSlideLength, actions, /^\/slideList\/(\d+)/);
        this.sectionModification = this.getModifiedPageOrSectionV2(
            oldSectionLength,
            actions,
            /^\/sectionList\/(\d+)/,
        );
        this.migrateV2toV3(presentationNew);
    }

    // 生成v2 keyframe
    private makeKeyframeV2(presentationText: string) {
        // 原始数据 presentationText 可能经过编码需要 unescape 一下
        // 但是若没有经过编码，unescape 再 JSON.parse 会解析出错（unescape会把一些必要的‘\’去掉）
        // 利用 try catch 兼容两种情况
        try {
            const decodeText = unescape(presentationText);
            return JSON.parse(decodeText);
        } catch {
            return JSON.parse(presentationText);
        }
    }

    // 迁移
    private migrateV2toV3(presentation) {
        const migrator = new SlideDataMigrator();
        const newPresentation = migrator.migratePresentation(presentation, '');
        const presentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        assert(presentationStoreService, 'presentationStoreService is not exist');
        this.presentationStoreService = presentationStoreService.createFromData(newPresentation);
        this.presentation = this.presentationStoreService.getPresentation();
        this.changedComponents.forEach((changed) => {
            const set = new Set();
            (changed.components as Set<string>).forEach((id) => {
                const ids = id.split('|');
                if (ids.length === 1) {
                    set.add(migrator.getPageId({ id: ids[0], type: 'slide' }));
                } else if (ids.length > 1) {
                    set.add(migrator.getId(['slide', ids[0], ids[ids.length - 1]].join(migrator.idSep), 'shape'));
                }
            });
            // eslint-disable-next-line no-param-reassign
            changed.components = set;
        });
    }

    /**
     * 根据旧版本长度（可能是 slide 长度，也可能是 section 长度）和 changeset 计算出哪些页被哪些人修改，以便高亮它们
     * @param {旧版本长度} oldLength
     * @param {changeset，包含作者id} actions
     * @param reg 在 cs 中匹配哪些字符串的 regex
     */
    private getModifiedPageOrSectionV2(oldSlideLength: number, actions, reg): Array<Array<string>> {
        const result: Array<any> = [];
        for (let i = 0; i < oldSlideLength; i++) {
            result.push([]);
        }
        actions.forEach((a) => {
            const { author } = a;
            const { changeset } = a;

            if (changeset.length > 0) {
                changeset.forEach((changes) => {
                    const [{ target }] = changes;
                    const isSlideChange = target === '';
                    changes.forEach((change) => {
                        const fullPath = `${change.target}${change.path}`;
                        const match = reg.exec(fullPath);
                        const index = match && match.length > 1 ? parseInt(match[1], 10) : -1;
                        if (index !== -1) {
                            if (isSlideChange) {
                                if (change.op === 'add') {
                                    result.splice(index, 0, [author]);
                                } else if (change.op === 'remove') {
                                    result.splice(index, 1);
                                } else if (change.op === 'move') {
                                    result[index] = [author];
                                }
                            } else {
                                const slide = result[index];
                                slide && slide.indexOf(author) === -1 && slide.push(author);
                            }
                        }
                    });
                });
            }
        });
        return result;
    }

    /**
     * 根据changeset查找变更组件的uniqueID
     * @param changesets changeset的数组
     * @returns uniqueID的Set
     */
    private findChangedComponentByChangesetV2(changeset: any): Set<string> {
        const set: Set<string> = new Set();
        if (!changeset) return set;
        changeset.forEach((patch) => {
            const array = this.parseIDOfComponentsByPatchV2(patch);
            array.forEach((id) => {
                set.add(id);
            });
        });
        return set;
    }

    /**
     * 根据patch解析变更的组件id
     * @param patch
    */
    private parseIDOfComponentsByPatchV2(patch: any): Array<string> {
        const children: string[] = [];
        const tid: string = _get(patch, '[0].tid');
        const getID = (tid, pid) => (tid ? `${tid}|${pid}` : pid);
        if (tid === null || tid === undefined) return children; // 异常情况
        const pathComponent = tid.split('|');
        if (pathComponent && pathComponent.length > 1) {
            // 如果操作对象非根节点、slide类型，直接返回tid
            children.push(tid);
            return children;
        }
        // 处理根节点、slide类型
        patch.forEach((operation) => {
            if (operation.op === 'add') {
                let id = _get(operation, 'value.id');
                if (!id && operation.value && operation.value.valueId) {
                    id = operation.value.valueId;
                }
                id && children.push(getID(tid, id));
            } else if (operation.op === 'move') {
                operation.fid && children.push(getID(tid, operation.fid));
            } else if (operation.pid) {
                children.push(getID(tid, operation.pid));
            }
        });
        return children;
    }
}
